// CRITICAL: Import production console first to mute logs in production
import './utils/productionConsole';

import "react-native-url-polyfill/auto";
import "react-native-get-random-values";
import { Alert, Platform, View, StyleSheet } from "react-native";
import React, { useEffect, useState } from "react";
import { StatusBar } from "expo-status-bar";
import { AppState, Linking } from "react-native";
import * as SplashScreen from "expo-splash-screen";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import Navigation from "./navigation/MainSwitchNavigator";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { AppRegistry } from "react-native";
import Toast from "react-native-toast-message";
import { ToastProvider } from "./contexts/ToastContext";
import { DeliveryAddressProvider } from "./contexts/DeliveryAddressContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { GuestModeProvider } from "./contexts/GuestModeContext";
import { useRealtimeManager } from "./hooks/useRealtimeManager";
require("node-libs-expo/globals");
import { NativeWindStyleSheet } from "nativewind";
import NotificationHandler from "./components/NotificationHandler";
import { Provider, useDispatch } from "react-redux";
import { initializeRequest, signOutSuccess } from "./store/slices/authSlice";
import { store } from "./store";
import NavigationService from "./services/NavigationService";

// CRITICAL: Check for password reset URL BEFORE Supabase processes it
// This must happen at module load time, before Supabase client is created
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  const url = window.location.href;
  const path = window.location.pathname;
  const search = window.location.search;
  const hash = window.location.hash;
  
  // Check if this is a password reset URL
  const isPasswordReset = path.includes('password-reset') || 
                         path.includes('PasswordReset') ||
                         url.includes('type=recovery') ||
                         search.includes('type=recovery') ||
                         hash.includes('type=recovery') ||
                         url.includes('token_hash');
  
  if (isPasswordReset) {
    console.log('🔐 [PASSWORD-RESET] Early detection: Password reset URL detected at module load', {
      url, path, search, hash
    });
    // Clear completed flag if we're starting a NEW password reset flow
    sessionStorage.removeItem('passwordResetCompleted');
    // Set flag in sessionStorage BEFORE Supabase processes the URL
    sessionStorage.setItem('passwordResetFlow', 'true');
    console.log('🔐 [PASSWORD-RESET] Early detection: Set passwordResetFlow flag in sessionStorage');
    
    // Notify other tabs using localStorage (which triggers storage events in other tabs)
    // Use a timestamp to ensure the event fires even if the value is the same
    localStorage.setItem('jadetrade_password_reset', Date.now().toString());
    console.log('🔐 [PASSWORD-RESET] Notified other tabs about password reset via localStorage');
  } else {
    // Not on password reset URL - clear any leftover flags
    sessionStorage.removeItem('passwordResetCompleted');
    sessionStorage.removeItem('passwordResetFlow');
  }
  
  // Listen for password reset events from other tabs via localStorage
  // This event fires in OTHER tabs when localStorage is changed
  window.addEventListener('storage', (event) => {
    if (event.key === 'jadetrade_password_reset' && event.newValue) {
      console.log('🔐 [PASSWORD-RESET] Received password reset notification from another tab');
      // If this tab is NOT the one doing password reset, mark it to ignore auth changes
      const thisTabIsPasswordReset = sessionStorage.getItem('passwordResetFlow') === 'true';
      if (!thisTabIsPasswordReset) {
        console.log('🔐 [PASSWORD-RESET] This tab is not doing password reset, setting ignore flag');
        // Set a flag to ignore Supabase auth state changes from the other tab
        sessionStorage.setItem('ignoreAuthChanges', 'true');
        // Don't do anything else - let the user stay where they are
        // The password reset tab will handle everything
      }
    } else if (event.key === 'jadetrade_password_reset_completed' && event.newValue) {
      console.log('🔐 [PASSWORD-RESET] Password reset completed in another tab');
      // Clear ALL the ignore/password reset flags
      sessionStorage.removeItem('ignoreAuthChanges');
      sessionStorage.removeItem('passwordResetFlow');
      sessionStorage.removeItem('passwordResetCompleted');
      // Also clear localStorage flags so they don't interfere after reload
      localStorage.removeItem('jadetrade_password_reset');
      localStorage.removeItem('jadetrade_password_reset_completed');
      // Sign out and reload to show login screen
      import('./lib/supabase').then(({ supabase }) => {
        supabase.auth.signOut().then(() => {
          window.location.href = '/';
        });
      }).catch(() => {
        window.location.href = '/';
      });
    }
  });
}

// Platform-specific imports
let Sentry: any = null;
let StripeProvider: any = null;
let Notifications: any = null;

// Import Sentry only on native
if (Platform.OS !== 'web') {
  try {
    Sentry = require('@sentry/react-native');
Sentry.init({
  dsn: 'https://fb04c6596fdb3ea13bdaffb5707d5263@o4510336777715712.ingest.us.sentry.io/4510421207941120',
  sendDefaultPii: true,
  enableLogs: true,
  integrations: [Sentry.feedbackIntegration()],
    });
  } catch (e) {
    console.log('[App] Sentry not available on this platform');
  }
}

// Import Stripe provider based on platform
if (Platform.OS === 'web') {
  try {
    const { StripeProviderWeb } = require('./components/StripeProviderWeb');
    StripeProvider = StripeProviderWeb;
  } catch (e) {
    console.log('[App] Web Stripe provider not available');
  }
} else {
  try {
    const stripeReactNative = require('@stripe/stripe-react-native');
    StripeProvider = stripeReactNative.StripeProvider;
  } catch (e) {
    console.log('[App] Native Stripe provider not available');
  }
}

// Import notifications only on native
if (Platform.OS !== 'web') {
  try {
    Notifications = require('expo-notifications');

// Configure notification handler for both iOS and Android
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

// Ensure Android notification channel is created early for consistent behavior
if (Platform.OS === 'android') {
  Notifications.setNotificationChannelAsync('default', {
    name: 'Default',
    importance: Notifications.AndroidImportance.MAX,
    vibrationPattern: [0, 250, 250, 250],
    lightColor: '#FF231F7C',
      }).catch((err: Error) => {
    console.warn('Failed to create Android notification channel:', err);
  });
    }
  } catch (e) {
    console.log('[App] expo-notifications not available on this platform');
  }
}

// Import webcrypto polyfill only on native (not needed on web)
if (Platform.OS !== 'web') {
  try {
    require('react-native-webcrypto');
  } catch (e) {
    console.log('[App] react-native-webcrypto not available');
  }
}

// Configure QueryClient optimized for real-time messaging
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds (shorter for real-time data)
      gcTime: 1000 * 60 * 5, // 5 minutes (reduced garbage collection time)
      refetchOnWindowFocus: false, // Rely on real-time subscriptions
      refetchOnMount: false, // Rely on real-time subscriptions
      refetchOnReconnect: true, // Refetch when reconnecting to network
      retry: 1, // Only retry once to avoid delays
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Max 5s delay
    },
    mutations: {
      retry: 2, // Retry mutations twice
      retryDelay: 1000, // 1 second delay between retries
    },
  },
});

// Re-export supabase for backwards compatibility (imported from lib/supabase)
export { supabase };

// Only add AppState listener on native platforms
if (Platform.OS !== 'web') {
AppState.addEventListener("change", (state) => {
  if (state === "active") {
    supabase.auth.startAutoRefresh();
  } else {
    supabase.auth.stopAutoRefresh();
  }
});
}

// Stripe publishable key from environment variable
const stripePublishableKey = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY;

if (!stripePublishableKey) {
  console.error('⚠️ [STRIPE] EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY is not set!');
}

function RootApp() {
  const dispatch = useDispatch();
  
  // Initialize centralized real-time manager
  useRealtimeManager();

  useEffect(() => {
    console.log("PK:", stripePublishableKey); // pk_test или pk_live
    // Start checking if we have a valid session
    dispatch(initializeRequest());
  }, [dispatch]);

  return <Navigation />;
}

const APP_SCHEMES = ["com.jadetrade", "jadepanda"]; // support both while migrating

// helpers for unified deep link handling
function getParam(u: URL, key: string) {
  const fromSearch = u.searchParams.get(key);
  if (fromSearch) return fromSearch;
  const hash = u.hash?.startsWith("#") ? u.hash.slice(1) : u.hash || "";
  const parts = new URLSearchParams(hash);
  return parts.get(key);
}

// Web-safe alert function
const showAlert = (title: string, message: string, buttons?: any[]) => {
  if (Platform.OS === 'web') {
    window.alert(`${title}\n\n${message}`);
    if (buttons?.[0]?.onPress) {
      buttons[0].onPress();
    }
  } else {
    Alert.alert(title, message, buttons);
  }
};

async function handleSupabaseAuthLink(url: string, navigation?: any) {
  try {
    const u = new URL(url);
    const scheme = u.protocol.replace(":", "").toLowerCase();
    
    // On web, also check for http/https
    const isValidScheme = Platform.OS === 'web' 
      ? ['http', 'https'].some(s => scheme.startsWith(s)) || APP_SCHEMES.some(s => scheme.startsWith(s))
      : APP_SCHEMES.some(s => scheme.startsWith(s));
    
    if (!isValidScheme) return;

    // Handle errors from Supabase first
    const error = getParam(u, "error");
    const errorCode = getParam(u, "error_code");
    const errorDescription = getParam(u, "error_description");
    if (error) {
      console.warn("Auth link error:", { error, errorCode, errorDescription });
      Toast.show({
        type: "error",
        text1: errorCode === "otp_expired" ? "Reset Link Expired" : "Link Error",
        text2:
          errorDescription ||
          (errorCode === "otp_expired"
            ? "Please request a new password reset email."
            : "Invalid or expired link."),
      });
      return;
    }

    const type = (getParam(u, "type") as "signup" | "recovery" | "email_change" | null) ?? null;
    const token = getParam(u, "token_hash") || getParam(u, "token") || "";
    const email = getParam(u, "email") || undefined;

    if (!type || !token) return; // not our link

    // Verify token (recovery/signup)
    const payload: any = { type, token_hash: token };
    if (email) payload.email = email;

    const { error: vErr } = await supabase.auth.verifyOtp(payload);
    if (vErr) {
      console.warn("verifyOtp error", vErr);
      Toast.show({
        type: "error",
        text1: "Verification failed",
        text2: vErr.message ?? "Please request a new link.",
      });
      return;
    }

    if (type === "recovery") {
      Toast.show({ type: "success", text1: "Link confirmed", text2: "Set a new password." });
      
      // Set flag in sessionStorage so MainSwitchNavigator can detect it
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        sessionStorage.setItem('passwordResetFlow', 'true');
        console.log('🔐 [PASSWORD-RESET] Set passwordResetFlow flag in sessionStorage');
      }
      
      // Navigate to PasswordReset screen
      const navigateToPasswordReset = () => {
        if (NavigationService.navigationRef.isReady()) {
          console.log('🔐 [PASSWORD-RESET] Navigating to PasswordReset from handleSupabaseAuthLink');
          // Use reset to ensure we're on the AuthFlow stack
          NavigationService.navigationRef.reset({
            index: 0,
            routes: [
              {
                name: 'AuthFlow',
                params: {
                  screen: 'PasswordReset'
                }
              }
            ],
          });
        } else {
          // If navigation not ready, try again after a short delay
          setTimeout(navigateToPasswordReset, 100);
        }
      };
      
      // Small delay to ensure MainSwitchNavigator has processed the flag
      setTimeout(navigateToPasswordReset, 300);
      return;
    }

    if (type === "signup") {
      const { data: s } = await supabase.auth.getSession();
      Toast.show({
        type: "success", text1: "Email confirmed", text2: s?.session ? "Signed in." : "You can sign in now.",
      });
      // @ts-ignore
      navigation?.navigate?.("Home");
      return;
    }

    Toast.show({ type: "success", text1: "Verified" });
  } catch (e: any) {
    console.error("URL parsing/handle error:", e);
    Toast.show({ type: "error", text1: "Link error", text2: "Invalid link format" });
  }
}

SplashScreen.preventAutoHideAsync().catch(() => {});

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [appIsReady, setAppIsReady] = useState(false);

  // Handle OAuth redirect on web
  useEffect(() => {
    if (Platform.OS === 'web') {
      // Check URL for OAuth callback or password reset
      const currentUrl = window.location.href;
      const currentPath = window.location.pathname;
      
      // Check if we're on the password-reset path or have auth params
      // Support /PasswordReset (matching iOS/Android pattern) - React Navigation will handle routing
      const isPasswordResetPath = currentPath === '/PasswordReset' || 
                                 currentPath === '/password-reset' ||
                                 currentPath === '/auth/password-reset';
      const hasAuthParams = currentUrl.includes('access_token') || 
                           currentUrl.includes('refresh_token') ||
                           currentUrl.includes('token_hash') || 
                           currentUrl.includes('type=recovery') ||
                           currentUrl.includes('error');
      
      // If we're on /PasswordReset, redirect to /auth/password-reset to match linking config
      // But preserve the query params and hash for token verification
      if (currentPath === '/PasswordReset' || currentPath === '/password-reset') {
        const newUrl = `${window.location.origin}/auth/password-reset${window.location.search}${window.location.hash}`;
        // Only redirect if we're not already on the target path
        if (currentPath !== '/auth/password-reset') {
          window.history.replaceState({}, '', newUrl);
        }
      }
      
      // Handle password reset URL - either via path or query params
      if (isPasswordResetPath || hasAuthParams) {
        // Verify token first, then let React Navigation linking handle the routing
        // The URL redirect above ensures React Navigation can route correctly
        setTimeout(() => {
          handleSupabaseAuthLink(window.location.href);
        }, 100);
      }
      
      // Listen for hash changes (OAuth redirects often use hash)
      const handleHashChange = () => {
        const newUrl = window.location.href;
        const newPath = window.location.pathname;
        const isPasswordResetPath = newPath === '/PasswordReset' || 
                                   newPath === '/password-reset' ||
                                   newPath === '/auth/password-reset';
        const hasAuthParams = newUrl.includes('access_token') || 
                             newUrl.includes('refresh_token') ||
                             newUrl.includes('token_hash') || 
                             newUrl.includes('type=recovery') ||
                             newUrl.includes('error');
        if (isPasswordResetPath || hasAuthParams) {
          handleSupabaseAuthLink(newUrl);
        }
      };
      
      // Listen for popstate (back/forward navigation)
      const handlePopState = () => {
        // Don't trigger navigation on popstate - let React Navigation handle it
        // This prevents unwanted navigation when returning from Stripe window
      };
      
      window.addEventListener('hashchange', handleHashChange);
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('hashchange', handleHashChange);
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, []);

  useEffect(() => {
    // Native deep link handling
    if (Platform.OS === 'web') return;
    
    const onUrl = (evt: { url: string }) => {
      console.log("Deep link event:", evt.url);
      handleSupabaseAuthLink(evt.url);
      if (evt.url.includes("stripe")) {
        console.log("STRIPE REDIRECT DETECTED:", evt.url);
      }
      if (evt.url.includes("jadepanda://") && evt.url.includes("error=")) {
        console.log("Detected error in OAuth redirect URL");
        if (evt.url.includes("unexpected_failure")) {
          console.log("Detected unexpected_failure error, attempting recovery...");
          supabase.auth.getSession().then(({ data: sessionData }) => {
            if (sessionData?.session) {
              console.log("Successfully recovered session despite error");
              setSession(sessionData.session);
              supabase.auth.refreshSession();
            } else {
              console.log("Could not recover session, user may need to sign in again");
              showAlert(
                "Sign-In Issue",
                "There was a problem with the Google sign-in. Please try again or use email sign-in instead.",
                [{ text: "OK" }]
              );
            }
          });
        }
      }
    };

    Linking.getInitialURL().then((url) => {
      if (url) {
        console.log("App opened with initial URL:", url);
        onUrl({ url });
      }
    });

    const sub = Linking.addEventListener("url", onUrl);
    return () => sub.remove();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const prepareApp = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (!isMounted) {
          return;
        }

        if (error) {
          console.warn("Session error (clearing invalid token):", error.message);
          await supabase.auth.signOut().catch(() => {});
          setSession(null);
        } else {
          console.log(
            "Initial session check:",
            data.session ? "Logged in" : "Not logged in"
          );
          setSession(data.session);
        }
      } catch (error: any) {
        if (!isMounted) {
          return;
        }
        console.warn("Failed to get session (clearing invalid token):", error?.message ?? error);
        await supabase.auth.signOut().catch(() => {});
        setSession(null);
      } finally {
        if (isMounted) {
          setAppIsReady(true);
        }
      }
    };

    prepareApp();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, authSession) => {
      console.log('🔐 [AUTH] Auth state changed:', event);
      
      // Check if we should ignore auth changes (another tab is doing password reset OR we're in Stripe flow)
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const ignoreAuthChanges = sessionStorage.getItem('ignoreAuthChanges') === 'true';
        const stripeFlowActive = sessionStorage.getItem('stripeFlowActive') === 'true';
        
        // ALSO check localStorage directly for password reset from another tab
        // This handles the race condition where the storage event hasn't fired yet
        const anotherTabDoingPasswordReset = localStorage.getItem('jadetrade_password_reset');
        const thisTabDoingPasswordReset = sessionStorage.getItem('passwordResetFlow') === 'true';
        const shouldIgnoreForPasswordReset = (ignoreAuthChanges || (anotherTabDoingPasswordReset && !thisTabDoingPasswordReset));
        
        if (shouldIgnoreForPasswordReset && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          console.log('🔐 [PASSWORD-RESET] Ignoring auth change because another tab is doing password reset');
          // Set the ignore flag in case it wasn't set yet
          if (!ignoreAuthChanges) {
            sessionStorage.setItem('ignoreAuthChanges', 'true');
          }
          // Don't update session or navigate - just ignore this event entirely
          // CRITICAL: Don't even call setSession - this prevents any re-renders
          return;
        }
        
        // During Stripe flow, ignore auth events that would cause re-renders/resets
        if (stripeFlowActive && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          console.log('💳 [STRIPE] Ignoring auth event during Stripe flow:', event);
          return;
        }
      }
      
      // Only update session state for actual auth changes, not token refreshes
      // Token refresh doesn't change the session - it just refreshes the access token
      // Updating state here causes unnecessary re-renders that can interrupt payment flows
      // CRITICAL: This must come AFTER the ignore checks above
      if (event !== "TOKEN_REFRESHED") {
        setSession(authSession);
      }

      if (event === "SIGNED_IN") {
        // Check if this is a password reset flow
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const passwordResetFlag = sessionStorage.getItem('passwordResetFlow');
          if (passwordResetFlag === 'true') {
            console.log('🔐 [PASSWORD-RESET] Auth state: SIGNED_IN during password reset flow, navigating to PasswordReset');
            // Navigate to password reset screen
            const navigateToPasswordReset = () => {
              if (NavigationService.navigationRef.isReady()) {
                console.log('🔐 [PASSWORD-RESET] Navigating to PasswordReset from onAuthStateChange');
                NavigationService.navigationRef.reset({
                  index: 0,
                  routes: [
                    {
                      name: 'AuthFlow',
                      params: {
                        screen: 'PasswordReset'
                      }
                    }
                  ],
                });
              } else {
                setTimeout(navigateToPasswordReset, 100);
              }
            };
            setTimeout(navigateToPasswordReset, 300);
            return; // Don't dispatch initializeRequest yet
          }
          
          // Check if we're in Stripe flow - don't reinitialize
          const stripeFlowFlag = sessionStorage.getItem('stripeFlowActive');
          if (stripeFlowFlag === 'true') {
            console.log('💳 [STRIPE] Ignoring SIGNED_IN event during Stripe flow to prevent navigation reset');
            return; // Don't dispatch initializeRequest - we're already initialized
          }
        }
        
        // Identify user in PostHog for web (native handles this in useGoogleSignIn/useAppleSignIn)
        if (Platform.OS === 'web' && authSession?.user) {
          try {
            const { identifyUser, trackEvent } = require('./services/posthog');
            const userEmail = authSession.user.email || authSession.user.user_metadata?.email || 'unknown';
            const provider = authSession.user.app_metadata?.provider || 'unknown';
            
            // Check if this is a new user (created within last 5 seconds)
            const userCreatedAt = new Date(authSession.user.created_at);
            const now = new Date();
            const secondsSinceCreation = (now.getTime() - userCreatedAt.getTime()) / 1000;
            const isNewUser = secondsSinceCreation < 5;
            
            identifyUser(authSession.user.id, {
              email: userEmail,
            });
            
            trackEvent('user_signed_in', {
              user_id: authSession.user.id,
              email: userEmail,
              method: provider,
              platform: 'web',
              is_new_user: isNewUser,
            });
            
            // Track sign-up for new users
            if (isNewUser) {
              trackEvent('user_signed_up', {
                user_id: authSession.user.id,
                email: userEmail,
                method: provider,
                platform: 'web',
              });
            }
            
            console.log(`✅ [POSTHOG] Web user identified on SIGNED_IN event${isNewUser ? ' (new user)' : ''}`);
          } catch (error) {
            console.warn('⚠️ [POSTHOG] Failed to identify user on web:', error);
          }
        }
        
        store.dispatch(initializeRequest());
      } else if (event === "SIGNED_OUT") {
        // Reset PostHog on sign out for web
        if (Platform.OS === 'web') {
          try {
            const { resetPostHog } = require('./services/posthog');
            resetPostHog();
            console.log('✅ [POSTHOG] Web PostHog reset on SIGNED_OUT');
          } catch (error) {
            console.warn('⚠️ [POSTHOG] Failed to reset PostHog on web:', error);
          }
        }
        store.dispatch(signOutSuccess());
      } else if (event === "PASSWORD_RECOVERY") {
        // Supabase detected a password recovery token
        console.log('🔐 [PASSWORD-RESET] PASSWORD_RECOVERY event detected');
        
        // Check if another tab is handling password reset - if so, ignore this event
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const ignoreAuthChanges = sessionStorage.getItem('ignoreAuthChanges') === 'true';
          if (ignoreAuthChanges) {
            console.log('🔐 [PASSWORD-RESET] Ignoring PASSWORD_RECOVERY event because another tab is handling it');
            return;
          }
          sessionStorage.setItem('passwordResetFlow', 'true');
        }
        // Navigate to password reset screen
        const navigateToPasswordReset = () => {
          if (NavigationService.navigationRef.isReady()) {
            console.log('🔐 [PASSWORD-RESET] Navigating to PasswordReset from PASSWORD_RECOVERY event');
            NavigationService.navigationRef.reset({
              index: 0,
              routes: [
                {
                  name: 'AuthFlow',
                  params: {
                    screen: 'PasswordReset'
                  }
                }
              ],
            });
          } else {
            setTimeout(navigateToPasswordReset, 100);
          }
        };
        setTimeout(navigateToPasswordReset, 300);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (appIsReady) {
      SplashScreen.hideAsync().catch(() => {});
    }
  }, [appIsReady]);

  if (!appIsReady) {
    return null;
  }

  // Mobile container wrapper for web - makes it look like a phone
  const MobileContainer = ({ children }: { children: React.ReactNode }) => {
    if (Platform.OS !== 'web') {
      return <>{children}</>;
  }

  return (
      <View style={webStyles.outerContainer}>
        <View style={webStyles.phoneContainer}>
          {children}
        </View>
      </View>
    );
  };

  // Wrap content with Stripe provider
  const renderContent = () => (
    <MobileContainer>
        <GestureHandlerRootView style={{ flex: 1 }}>
          <SafeAreaProvider>
            <QueryClientProvider client={queryClient}>
              <ToastProvider>
                <GuestModeProvider>
                  <NotificationsProvider>
                    <DeliveryAddressProvider>
                      <NotificationHandler>
                        <RootApp />
                        <StatusBar style="light" />
                        <Toast />
                      </NotificationHandler>
                    </DeliveryAddressProvider>
                  </NotificationsProvider>
                </GuestModeProvider>
              </ToastProvider>
            </QueryClientProvider>
          </SafeAreaProvider>
        </GestureHandlerRootView>
    </MobileContainer>
  );

  // Wrap with Stripe provider if available
  const AppWithStripe = () => {
    if (StripeProvider && stripePublishableKey) {
      return (
        <StripeProvider
          publishableKey={stripePublishableKey}
          merchantIdentifier="merchant.com.jadetrade"
          urlScheme="com.jadetrade"
        >
          {renderContent()}
      </StripeProvider>
      );
    }
    return renderContent();
  };

  return (
    <Provider store={store}>
      <AppWithStripe />
    </Provider>
  );
}

// Web-specific styles for phone-like container
const webStyles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: '#111', // Dark background outside the phone
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100%',
    padding: 16,
  } as any,
  phoneContainer: {
    width: '100%',
    maxWidth: 430, // iPhone 14 Pro Max width
    height: '100%',
    maxHeight: 932, // iPhone 14 Pro Max height  
    backgroundColor: '#000',
    overflow: 'hidden',
    // Add rounded corners and shadow for device-like appearance
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#333',
    // Shadow for depth
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.5,
    shadowRadius: 30,
  } as any,
});

// Wrap with Sentry on native, or export directly on web
let ExportedApp = App;
if (Platform.OS !== 'web' && Sentry) {
  ExportedApp = Sentry.wrap(App);
}

export default ExportedApp;
